/**
 * Detached handle of the platform preferences window.
 *
 * The shell opens it with `workbench.openSettings`. Account logout and a
 * workspace switch ask the host to close this handle; other windows stay.
 */
export const SETTINGS_WINDOW_VIEW_ID = 'platform.settingsWindow';
export const SETTINGS_WINDOW_FRAGMENT_ID = 'platform.settingsWindow';

export const SETTINGS_WINDOW_HANDLE = {
  viewId: SETTINGS_WINDOW_VIEW_ID,
  fragmentId: SETTINGS_WINDOW_FRAGMENT_ID,
} as const;

/**
 * Ask an already-open preferences window to show a page. A window that is
 * still opening misses this broadcast; the same id rides the detach payload
 * so the first paint can read it from the URL.
 */
export const SETTINGS_WINDOW_SELECT_PAGE_EVENT =
  'platform.settingsWindow.selectPage';

/** Payload of {@link SETTINGS_WINDOW_SELECT_PAGE_EVENT} and the detach query. */
export interface SettingsWindowSelectPagePayload {
  readonly pageId: string;
}

/**
 * Page id from `workbench.openSettings`'s first argument, the detach payload,
 * or {@link SETTINGS_WINDOW_SELECT_PAGE_EVENT}. A missing or blank id means
 * the caller did not name a page.
 */
export function readSettingsPageId(value: unknown): string | undefined {
  if (typeof value === 'string') {
    const pageId = value.trim();
    return pageId.length > 0 ? pageId : undefined;
  }
  if (isPagePayload(value)) {
    const pageId = value.pageId.trim();
    return pageId.length > 0 ? pageId : undefined;
  }
  return undefined;
}

function isPagePayload(
  value: unknown,
): value is SettingsWindowSelectPagePayload {
  if (typeof value !== 'object' || value === null || !('pageId' in value)) {
    return false;
  }
  return typeof value.pageId === 'string';
}
